import { PageOptions } from '@graphcommerce/framer-next-pages'
import { flushMeasurePerf } from '@graphcommerce/graphql'
import {
  extractUrlQuery,
  parseParams,
  ProductListParams,
  getFilterTypes,
} from '@graphcommerce/magento-product'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, MetaRobots, useStorefrontConfig } from '@graphcommerce/next-ui'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { LayoutNavigation, LayoutNavigationProps } from '../../components'
import { InstantSearch, InstantSearchMeta } from '../../components/AlgoliaSearch'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { graphqlSsrClient, graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'

type SearchResultProps = { params: ProductListParams }
type RouteProps = { url: string[] }
export type GetPageStaticProps = GetStaticProps<
  LayoutNavigationProps,
  SearchResultProps,
  RouteProps
>

function SearchResultPage(props: SearchResultProps) {
  const storeB2B = useStorefrontConfig().magentoStoreCode.includes('b2b')
  const metaRobots = ['noindex', 'follow'] as MetaRobots[]
  const router = useRouter()
  const { params } = props

  // We need to use an useEffect here as we cant rely on NextJs to handle scroll restoration.
  // The page is loaded first, then the algolia results are loaded after that
  // The actual scroll restoration happens in ResultsList
  useEffect(() => {
    const handleRouteChange = (url) => {
      localStorage.setItem('clickedAlgoliaResult', url as string)
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router])

  return (
    <>
      <InstantSearchMeta storeB2B={storeB2B} metaRobots={metaRobots} />
      <InstantSearch query={params.search} />
    </>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}

SearchResultPage.pageOptions = pageOptions

export default SearchResultPage

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const [searchShort = '', query = []] = extractUrlQuery(params)
  const search = searchShort.length >= 3 ? searchShort : ''

  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const filterTypes = getFilterTypes(client)

  const staticClient = graphqlSsrClient(locale)
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const productListParams = parseParams(
    search ? `search/${search}` : 'search',
    query,
    await filterTypes,
    search,
  )

  if (!productListParams) return { notFound: true, revalidate: 60 * 20 }

  const result = {
    props: {
      ...(await layout)?.data,
      filterTypes: await filterTypes,
      params: productListParams,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
  flushMeasurePerf()
  return result
}
